import {ButtonProps, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {
  BaseSupplierOrderMaterialItem,
  PostSupplierOrderMaterialBasketItemRequest,
  useBulkPostSupplierOrderItemsMutation,
  useDeleteSupplierOrderItemsMutation,
  useDeleteSupplierOrderMaterialBasketItemsMutation,
  useGetSupplierOrderMaterialBasketQuery,
  usePatchSupplierOrderItemMutation,
  usePatchSupplierOrderMaterialBasketItemQuantityMutation,
  usePostSupplierOrderBasketCheckoutMutation,
  usePostSupplierOrderMaterialBasketItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useInvalidBasketItemsIds,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EDIT_SUPPLIER_ORDER_ITEM_DIALOG} from '../constants/editSupplierOrderItemDialog';
import {MaterialList} from './MaterialList';
import {SupplierOrderItemDetail} from './SupplierOrderItemDetail';

interface MaterialModalProps extends RequiredTestIdProps {
  orderId: string;
  onDiscard: VoidFunction;
}

export function MaterialModal(props: MaterialModalProps) {
  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetSupplierOrderMaterialBasketQuery({orderId: props.orderId});

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    usePostSupplierOrderBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDeleteSupplierOrderMaterialBasketItemsMutation();

  const [changeMaterialBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchSupplierOrderMaterialBasketItemQuantityMutation();

  const [addMaterialToBasket, materialResult] = usePostSupplierOrderMaterialBasketItemMutation();

  const [deleteMaterialItems, {isLoading: isDeletingItems}] = useDeleteSupplierOrderItemsMutation();

  const [addMaterialFromPurchaseListToBasket, materialFromPurchaseListResult] =
    useBulkPostSupplierOrderItemsMutation();

  const [, {isLoading: isEditingSupplierOrderItem}] = usePatchSupplierOrderItemMutation({
    fixedCacheKey: 'edit-supplier-order-item',
  });

  const basketItems = basket?.sparePartsBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.sparePartsBasketTotalPrice;

  const isAddingItemsToBasket =
    materialResult.isLoading || materialFromPurchaseListResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isCheckingOutBasket ||
    isDiscardingBasket ||
    isChangingQuantity ||
    isEditingSupplierOrderItem ||
    isDeletingItems;

  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);

  const handleCheckout = async () => {
    await checkoutBasket({
      orderId: props.orderId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await discardBasket({orderId: props.orderId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleAddMaterialToBasket = async (
    article: PostSupplierOrderMaterialBasketItemRequest['body']['article']
  ) => {
    await addMaterialToBasket({
      orderId: props.orderId,
      body: {
        article,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleAddMaterialFromPurchaseListToBasket = async (requestItemId: string[]) => {
    await addMaterialFromPurchaseListToBasket({
      orderId: props.orderId,
      body: {requestItemId, itemState: 'BASKET'},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeMaterialBasketItemQuantity({
      orderId: props.orderId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {itemId: ids};

    await deleteMaterialItems({
      orderId: props.orderId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError('itemId is not defined');
    }

    openDialog(
      <SupplierOrderItemDetail
        supplierOrderId={props.orderId}
        supplierOrderItemId={editingDetails.itemId}
        data-testid={suffixTestId('form.addItem', props)}
      />,
      {
        id: EDIT_SUPPLIER_ORDER_ITEM_DIALOG,
        title: i18n.t('general.labels.edit'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('dialog.addItem', props),
      }
    );
  };

  const actions: ButtonProps[] = [
    {
      isLoading: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: hasPendingRequest,
      isDisabled: isBasketEmpty || hasInvalidBasketItems,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.actions.addMaterial')} actions={actions}>
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <MaterialList
              onAddMaterialToBasket={handleAddMaterialToBasket}
              onAddMaterialFromPurchaseListToBasket={handleAddMaterialFromPurchaseListToBasket}
              hasPendingRequest={hasPendingRequest}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<BaseSupplierOrderMaterialItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{isMechanicHidden: true}}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
